<template>
  <div class="w-full max-w-2xl mx-auto grid gap-2">
    <Label>{{ label }} <span v-if="required" class="text-red-500">*</span></Label>
    
    <div
      class="relative min-h-[200px] rounded-lg border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center cursor-pointer"
      :class="[ 
        isDragging && !previews
          ? 'border-emerald-400 bg-emerald-400/5' 
          : 'border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10',
        (selectedFiles.length > 0 || previews) ? 'border-solid' : 'border-dashed'
      ]"
      @dragenter.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <input
        type="file"
        ref="fileInput"
        class="hidden"
        @change="handleFileSelect"
        accept="image/*,video/mp4"
        multiple
      />
      
      <div v-if="selectedFiles.length === 0 && !previews?.length" class="flex flex-col items-center p-6 text-center">
        <div class="p-4 rounded-full bg-white/10 mb-4">
          <LinkIcon class="w-8 h-8 text-primary" />
        </div>
        <p class="text-white">
          Selecione imagens ou vídeos
          <span class="block text-sm text-white/60">
            ou arraste e solte aqui
          </span>
        </p>
      </div>

      <!-- Exibição de múltiplos arquivos -->
      <div v-else class="w-full p-4">
        <div class="flex flex-wrap gap-3 items-center justify-center">
          <!-- Renderiza cada arquivo -->
          <div 
            v-for="(file, index) in filesToDisplay" 
            :key="index"
            class="relative w-24 h-24 border border-white/20 rounded-lg overflow-hidden group"
          >
            <!-- Imagem ou vídeo baseado no tipo -->
            <img 
              v-if="isImageFile(file)"
              :src="getFileUrl(file)"
              alt="Preview"
              class="w-full h-full object-cover"
            />
            <video 
              v-else
              :src="getFileUrl(file)"
              class="w-full h-full object-cover"
            />
            
            <!-- Botão de remover -->
            <button
              @click.stop="removeFile(index)"
              class="absolute top-1 right-1 p-1 rounded-full bg-red-500/80 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remover mídia"
            >
              <XIcon class="w-3 h-3 text-white" />
            </button>
            
            <!-- Badge de número -->
            <div class="absolute top-1 left-1 w-5 h-5 flex items-center justify-center bg-primary rounded-full text-xs text-white">
              {{ index + 1 }}
            </div>
          </div>
          
          <!-- Botão para adicionar mais arquivos quando já tem alguns -->
          <div 
            v-if="(selectedFiles.length + (previews?.length ?? 0)) > 0 && (selectedFiles.length + (previews?.length ?? 0)) < maxFiles"
            @click.stop="triggerFileInput"
            class="w-24 h-24 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center cursor-pointer hover:border-white/40 hover:bg-white/10"
          >
            <PlusIcon class="w-8 h-8 text-white/60" />
          </div>
        </div>
      </div>

      <!-- Barra de progresso -->
      <div
        v-if="uploading"
        class="absolute inset-x-0 bottom-0 bg-black/70 p-2"
      >
        <div 
          class="h-1 bg-emerald-400 transition-all duration-300"
          :style="{ width: `${progress}%` }"
        />
        <span class="block text-center text-sm text-white mt-1">
          {{ progress }}%
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { LinkIcon, XIcon, PlusIcon, UploadIcon } from 'lucide-vue-next'
import { Label } from '../label';
import { useToast } from '../toast';

const { toast } = useToast()

interface Props {
  label?: string
  modelValue?: File[] | null // Para v-model com múltiplos arquivos
  previews?: string[] // URLs das prévias existentes (opcional)
  maxFiles?: number
  maxSizeImage?: number // em MB
  maxSizeVideo?: number // em MB
  required?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  modelValue: () => [],
  maxFiles: 10,
  maxSizeImage: 5, // 5MB
  maxSizeVideo: 50, // 50MB
  required: false,
  previews:  () => []
})

const emit = defineEmits(['update:modelValue', 'update:previews'])

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const selectedFiles = ref<File[]>(Array.isArray(props.modelValue) ? [...props.modelValue] : [])
const fileUrls = ref<string[]>([])
const uploading = ref(false)
const progress = ref(0)

// Computed para exibir arquivos (seja do preview ou selecionados)
const filesToDisplay = computed(() => {
  return [...(props.previews || []), ...selectedFiles.value]
})

// Atualizar quando o modelValue mudar externamente
watch(() => props.modelValue, (newValue) => {
  if (newValue && Array.isArray(newValue)) {
    selectedFiles.value = [...newValue]
    updateFileUrls()
  } else {
    selectedFiles.value = []
    fileUrls.value = []
  }
}, { deep: true })

// Gerar URLs para os arquivos selecionados
function updateFileUrls() {
  // Limpar URLs antigos para evitar vazamento de memória
  fileUrls.value.forEach(url => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  })
  
  fileUrls.value = selectedFiles.value.map(file => URL.createObjectURL(file))
}

// Verificar se é uma imagem
function isImageFile(file: File | string): boolean {
  if (typeof file === 'string') {
    return file.startsWith('data:image') || /\.(jpeg|jpg|gif|png|avif|webp)$/i.test(file)
  }
  return file.type.startsWith('image/')
}

// Obter URL para exibição
function getFileUrl(file: File | string): string {
  if (typeof file === 'string') {
    return file
  }
  
  const index = selectedFiles.value.indexOf(file)
  if (index !== -1 && index < fileUrls.value.length) {
    return fileUrls.value[index]
  }
  return ''
}

const triggerFileInput = () => {
  if ((selectedFiles.value.length + (props.previews?.length ?? 0)) < props.maxFiles) {
    fileInput.value?.click()
  }
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files
  
  if (!files || files.length === 0) return
  
  // Converter FileList para array e processar
  const fileArray = Array.from(files)
  processFiles(fileArray)
  
  // Limpar input para permitir selecionar o mesmo arquivo novamente
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  
  if (!event.dataTransfer?.files || event.dataTransfer.files.length === 0) return
  
  const fileArray = Array.from(event.dataTransfer.files)
  processFiles(fileArray)
}

const processFiles = (files: File[]) => {
  // Verificar se não excede o limite máximo
  const remainingSlots = props.maxFiles - (selectedFiles.value.length + (props.previews?.length ?? 0))
  if (remainingSlots <= 0) {
    toast({
      variant: 'destructive',
      description: `Você atingiu o limite máximo de ${props.maxFiles} arquivos.`
    })
    return
  }
  
  // Limitar ao número de slots disponíveis
  const filesToProcess = files.slice(0, remainingSlots)
  
  // Validar cada arquivo
  const validFiles: File[] = []
  
  for (const file of filesToProcess) {
    // Verificar tipo
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast({
        variant: 'destructive',
        description: 'Por favor, selecione apenas arquivos de imagem ou vídeo.'
      })
      continue
    }
    
    // Verificar tamanho
    const maxSize = file.type.startsWith('image/') ? props.maxSizeImage : props.maxSizeVideo
    if (file.size > maxSize * 1024 * 1024) {
      toast({
        variant: 'destructive',
        description: `O arquivo ${file.name} excede o tamanho máximo de ${maxSize}MB.`
      })
      continue
    }

    validFiles.push(file)
  }
  
  if (validFiles.length > 0) {
    // Adicionar aos arquivos selecionados
    selectedFiles.value = [...selectedFiles.value, ...validFiles]
    updateFileUrls()
    emit('update:modelValue', selectedFiles.value)
    simulateUpload()
  }
}

const removeFile = (index: number) => {
  if (filesToDisplay.value[index].toString().includes('http')) {
    const previews = props.previews?.filter(preview => preview !== filesToDisplay.value[index])
    emit('update:previews', previews)
    return
  }
  
  // Remover URL do objeto
  if ((index - (props.previews?.length ?? 0)) < fileUrls.value.length) {
    URL.revokeObjectURL(fileUrls.value[(index - (props.previews?.length ?? 0))])
  }
  
  // Remover arquivo
  selectedFiles.value.splice((index - (props.previews?.length ?? 0)), 1)
  fileUrls.value.splice((index - (props.previews?.length ?? 0)), 1)
  
  emit('update:modelValue', selectedFiles.value.length > 0 ? selectedFiles.value : null)
}

const simulateUpload = () => {
  uploading.value = true
  progress.value = 0
  
  const interval = setInterval(() => {
    progress.value += 5
    if (progress.value >= 100) {
      clearInterval(interval)
      uploading.value = false
    }
  }, 50)
}
</script>